// Renewals due — dashboard panel aggregating Expiring soon / Expired items
// across Training, Competency and Company Compliance. Phase 8.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ArrowUpRight, GraduationCap, Award, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const MODULE_META = {
    training: { icon: GraduationCap, label: "Training" },
    competency: { icon: Award, label: "Competency" },
    company: { icon: ShieldCheck, label: "Company" },
};

function daysLabel(d) {
    if (d === null || d === undefined) return "";
    if (d < 0) return `${Math.abs(d)}d overdue`;
    if (d === 0) return "due today";
    return `in ${d}d`;
}

export default function RenewalsPanel() {
    const [data, setData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => { api.get("/renewals").then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} })); }, []);

    if (!data) return null;
    const { items, counts } = data;

    return (
        <div data-testid="renewals-panel" className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center justify-between border-b" style={{ borderColor: "#F0EDE8" }}>
                <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                    <div>
                        <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Renewals due</h3>
                        <p className="text-[11.5px]" style={{ color: "#6F6A64" }}>Training, competency &amp; accreditation expiries</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {counts.expired > 0 && <span data-testid="renewals-expired-count" className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: "#FBE8E5", color: "#B33A2E" }}>{counts.expired} expired</span>}
                    {counts.expiring_soon > 0 && <span data-testid="renewals-soon-count" className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ background: "#FCF1DC", color: "#C68A1C" }}>{counts.expiring_soon} due soon</span>}
                </div>
            </div>

            {items.length === 0 ? (
                <div className="px-5 py-8 text-center text-[12.5px] italic" style={{ color: "#9A968F" }}>Nothing expiring — all renewals are up to date.</div>
            ) : (
                <ul className="divide-y" style={{ borderColor: "#F0EDE8" }}>
                    {items.slice(0, 8).map((it) => {
                        const m = MODULE_META[it.module] || MODULE_META.training;
                        const Icon = m.icon;
                        const expired = it.status === "Expired";
                        return (
                            <li key={`${it.module}-${it.id}`} data-testid={`renewal-item-${it.id}`} onClick={() => navigate(it.route)}
                                className="px-5 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-[#FCFBF9] transition-colors">
                                <span className="w-7 h-7 rounded-[6px] grid place-items-center shrink-0" style={{ background: "#F6F4F0" }}>
                                    <Icon className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{it.title}</div>
                                    <div className="text-[10.5px] truncate" style={{ color: "#9A968F" }}>{m.label}{it.subtitle ? ` · ${it.subtitle}` : ""}</div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-[11px] font-bold" style={{ color: expired ? "#B33A2E" : "#C68A1C" }}>{daysLabel(it.days_to_expiry)}</div>
                                    <div className="text-[10px]" style={{ color: "#9A968F" }}>{it.expiry_date || ""}</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            {items.length > 8 && (
                <div className="px-5 py-2.5 border-t text-center" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={() => navigate("/app/training")} className="text-[11.5px] font-semibold inline-flex items-center gap-1 text-[var(--brand-accent,_#C75A1B)] hover:underline">
                        View all {counts.total} renewals <ArrowUpRight className="w-3 h-3" />
                    </button>
                </div>
            )}
        </div>
    );
}
